import {
  Divider,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import {
  ChevronDownFilled,
  ChevronLeftFilled,
  ChevronRightFilled,
  ChevronUpFilled,
} from "@fluentui/react-icons";
import React from "react";
import { ISignForm, SymbolPlacement } from "../../interfaces/ISignForm";
import {
  calculateScaledDimensions,
  symbols,
} from "../../helper/signGeneratorHelper";
type SignProps = {
  sign: ISignForm;
  signRef?: React.MutableRefObject<HTMLDivElement>;
  modalPreview?: boolean;
  isTemplate?: boolean;
  landingPage?: boolean;
};
const useStyles = makeStyles({
  flex: {
    display: "flex",
  },
  sign: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  border: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: "none",
  },
  signSelect: {
    transition: "transform 0.3s ease, background-color 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
      backgroundColor: "#A9A9A9",
      cursor: "pointer",
    },
  },
  symbol: {
    position: "absolute",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    zIndex: 2,
  },
  text: {
    position: "absolute",
    whiteSpace: "pre-wrap",
    lineHeight: 1.2,
    zIndex: 1,
  },
  horiziontalWidth: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  verticalHeight: {
    display: "flex",
    alignItems: "center",
    marginTop: "8px",
  },
  verticalText: {
    writingMode: "vertical-rl",
    textAlign: "center",
  },
  font: {
    fontFamily: "Tratex, Arial, sans-serif !important",
  },
  specialChar: {
    fontFamily: "Arial, sans-serif !important",
    fontWeight: "bold",
  },
  divider: {
    "::before": {
      borderTopWidth: tokens.strokeWidthThick,
    },
    "::after": {
      borderTopWidth: tokens.strokeWidthThick,
    },
  },
});
export const Sign = ({
  sign,
  signRef,
  modalPreview,
  isTemplate,
  landingPage,
}: SignProps) => {
  const styles = useStyles();

  const originalWidth = sign.width;
  const originalHeight = sign.height;

  const maxDimension = landingPage
    ? window.innerWidth < 1700
      ? 150
      : 250
    : modalPreview || isTemplate
    ? window.innerWidth < 1700
      ? 250
      : 300
    : window.innerWidth < 1700
    ? 500
    : 800;

  const { width, height, scaleFactor } = calculateScaledDimensions(
    originalWidth,
    originalHeight,
    maxDimension
  );

  const borderMm = originalWidth <= 600 ? 15 : originalWidth <= 1200 ? 20 : 25;
  const borderPx = borderMm * scaleFactor;

  const texts = sign?.texts || [];
  const symbol = sign?.symbol;

  return (
    <div className={styles.flex} ref={signRef}>
      <div>
        <div
          className={mergeClasses(
            styles.sign,
            isTemplate && !landingPage && styles.signSelect
          )}
          style={{
            width: `${width}px`,
            height: `${height}px`,
            backgroundColor: sign.primaryColorCode,
          }}
        >
          <div
            className={styles.border}
            style={{
              borderRadius: `${40 * scaleFactor}px`,
              border: `${borderPx}px solid ${sign.secondaryColorCode}`,
            }}
          ></div>

          {symbol && (
            <div
              className={styles.symbol}
              style={{
                ...(sign.symbolPlacement === SymbolPlacement.TOP_LEFT
                  ? { top: `${borderPx + 25 * scaleFactor}px` }
                  : { top: "50%", transform: "translateY(-50%)" }),
                left: `${borderPx * 2}px`,
                width: `${symbol.width * scaleFactor}px`,
                height: `${symbol.height * scaleFactor}px`,
                backgroundImage: `url(${
                  symbols().find((s) => s.name === symbol?.name)?.symbol
                })`,
              }}
            ></div>
          )}

          {texts.length > 0 && (
            <div
              className={styles.text}
              style={{
                top: "50%",
                left: symbol
                  ? `${borderPx * 2 + symbol.width * scaleFactor}px`
                  : `${borderPx + 1}px`,
                right: `${borderPx + 1}px`,
                transform: "translateY(-50%)",
                textAlign: "center",
                width: symbol
                  ? `calc(100% - ${borderPx * 3 + symbol.width * scaleFactor}px)`
                  : `calc(100% - ${borderPx * 2 + 1}px)`,
                position: "absolute",
              }}
            >
              {texts
                .slice()
                .sort((a, b) => a.row - b.row)
                .map((textItem, idx) => (
                  <div
                    className={styles.font}
                    key={idx}
                    style={{
                      fontSize: `${(textItem.fontSize * scaleFactor) / 0.8}px`,
                      letterSpacing: `${width * 0.0075}px`,
                      color: textItem.color,
                      wordBreak: "break-word",
                    }}
                  >
                    {textItem?.text
                      ? textItem.text.split("").map((char, index) =>
                          ["=", "%", "#", "/", "!", "?"].includes(char) ? (
                            <span key={index} className={styles.specialChar}>
                              {char}
                            </span>
                          ) : (
                            <span key={index} className={styles.font}>
                              {char}
                            </span>
                          )
                        )
                      : "\u00A0"}
                  </div>
                ))}
            </div>
          )}
        </div>

        {!isTemplate && (
          <div
            className={styles.verticalHeight}
            style={{
              width: `${width}px`,
            }}
          >
            <ChevronLeftFilled />
            <Divider
              appearance="brand"
              style={{ color: "black" }}
              className={styles.divider}
            >{`${originalWidth}mm`}</Divider>
            <ChevronRightFilled />
          </div>
        )}
      </div>
      {!isTemplate && (
        <div
          className={styles.horiziontalWidth}
          style={{
            height: `${height}px`,
          }}
        >
          <ChevronUpFilled />
          <Divider
            vertical
            appearance="brand"
            style={{ color: "black" }}
            className={styles.divider}
          >
            <p className={styles.verticalText}>{`${originalHeight}mm`}</p>
          </Divider>
          <ChevronDownFilled />
        </div>
      )}
    </div>
  );
};
